import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Bell, Check } from "lucide-react";
import { toast } from "sonner";

interface NotificationPlannerProps {
  prescriptionItems: Array<{
    prescription: string;
    dosage?: string;
    duration?: string;
    frequency?: string;
    id?: string;
  }>;
  onSave: (config: {
    notifyEmail: boolean;
    notifyWhatsApp: boolean;
    notifySMS: boolean;
    itemsConfig: Record<string, any>;
  }) => Promise<void>;
}

interface ItemConfig {
  frequency: string;
  period: string;
  time: string;
  weekdays: string[];
  durationDays: string;
}

const weekdayOptions = [
  { value: "seg", label: "Seg" },
  { value: "ter", label: "Ter" },
  { value: "qua", label: "Qua" },
  { value: "qui", label: "Qui" },
  { value: "sex", label: "Sex" },
  { value: "sab", label: "Sáb" },
  { value: "dom", label: "Dom" },
];

export function NotificationPlanner({ prescriptionItems, onSave }: NotificationPlannerProps) {
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyWhatsApp, setNotifyWhatsApp] = useState(false);
  const [notifySMS, setNotifySMS] = useState(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [itemsConfig, setItemsConfig] = useState<Record<string, ItemConfig>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [saveResult, setSaveResult] = useState<string | null>(null);

  const hasMethodSelected = notifyEmail || notifyWhatsApp || notifySMS;

  // Parse frequência da receita (ex: "1x ao dia" → { frequency: "1", period: "day" })
  const parseFrequency = (frequencyStr?: string): { frequency: string; period: string } => {
    if (!frequencyStr) return { frequency: "1", period: "day" };

    const lower = frequencyStr.toLowerCase();

    // Extrair número (1x, 2x, 3x, etc.)
    const freqMatch = lower.match(/(\d+)/);
    const freq = freqMatch ? freqMatch[1] : "1";

    // Identificar período
    let period = "day";
    if (lower.includes("semana")) period = "week";
    else if (lower.includes("mês") || lower.includes("mes")) period = "month";
    else if (lower.includes("ano")) period = "year";

    return { frequency: Math.min(parseInt(freq), 3).toString(), period };
  };

  // Parse duração da receita (ex: "30 dias" → "30", "Contínuo" → "continuous")
  const parseDuration = (durationStr?: string): string => {
    if (!durationStr) return "30";

    const lower = durationStr.toLowerCase();

    if (lower.includes("contínuo") || lower.includes("continuo") || lower.includes("uso contínuo")) {
      return "continuous";
    }

    // Extrair número de dias
    const daysMatch = lower.match(/(\d+)/);
    return daysMatch ? daysMatch[1] : "30";
  };

  const toggleItem = (itemName: string) => {
    const newSelected = selectedItems.includes(itemName)
      ? selectedItems.filter((i) => i !== itemName)
      : [...selectedItems, itemName];
    setSelectedItems(newSelected);

    // Initialize config for newly selected items
    if (!selectedItems.includes(itemName)) {
      // Buscar o item correspondente
      const item = prescriptionItems.find((i) => i.prescription === itemName);

      // Fazer parsing dos dados da receita
      const { frequency, period } = parseFrequency(item?.frequency);
      const durationDays = parseDuration(item?.duration);

      setItemsConfig((prev) => ({
        ...prev,
        [itemName]: {
          frequency,
          period,
          time: "08:00",
          weekdays: weekdayOptions.map((w) => w.value),
          durationDays,
        },
      }));
    } else {
      // Remove config if deselected
      const newConfig = { ...itemsConfig };
      delete newConfig[itemName];
      setItemsConfig(newConfig);
    }
  };

  const updateItemConfig = (itemName: string, field: keyof ItemConfig, value: any) => {
    setItemsConfig((prev) => ({
      ...prev,
      [itemName]: {
        ...prev[itemName],
        [field]: value,
      },
    }));
  };

  const toggleWeekday = (itemName: string, weekday: string) => {
    const config = itemsConfig[itemName];
    const newWeekdays = config.weekdays.includes(weekday)
      ? config.weekdays.filter((w) => w !== weekday)
      : [...config.weekdays, weekday];

    updateItemConfig(itemName, "weekdays", newWeekdays);
  };

  const handleSave = async () => {
    if (!notifyEmail && !notifyWhatsApp && !notifySMS) {
      toast.error("Selecione pelo menos um método de notificação");
      return;
    }

    if (selectedItems.length === 0) {
      toast.error("Selecione pelo menos um item para notificar");
      return;
    }

    setIsSaving(true);
    setSaveResult(null);

    try {
      await onSave({
        notifyEmail,
        notifyWhatsApp,
        notifySMS,
        itemsConfig,
      });

      // Build result message
      const methods = [];
      if (notifyEmail) methods.push("E-mail");
      if (notifyWhatsApp) methods.push("WhatsApp");
      if (notifySMS) methods.push("SMS");

      const resultText = `${selectedItems.length} notificação(ões) configurada(s) com sucesso via ${methods.join(", ")}!`;
      setSaveResult(resultText);
      toast.success("Notificações salvas com sucesso!");
    } catch (error) {
      console.error("Erro ao salvar notificações:", error);
      toast.error("Erro ao salvar notificações");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className="p-6 bg-card border-border">
      <div className="space-y-6">
        <div className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-foreground">Planejar Administração do Paciente</h3>
        </div>

        {/* Métodos de Notificação */}
        <div className="space-y-3">
          <Label className="text-sm font-semibold text-foreground">Método de Notificação</Label>
          <div className="flex gap-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="notify-email"
                checked={notifyEmail}
                onCheckedChange={(checked) => setNotifyEmail(checked as boolean)}
              />
              <Label htmlFor="notify-email" className="cursor-pointer text-foreground">
                E-mail
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="notify-whatsapp"
                checked={notifyWhatsApp}
                onCheckedChange={(checked) => setNotifyWhatsApp(checked as boolean)}
              />
              <Label htmlFor="notify-whatsapp" className="cursor-pointer text-foreground">
                WhatsApp
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="notify-sms"
                checked={notifySMS}
                onCheckedChange={(checked) => setNotifySMS(checked as boolean)}
              />
              <Label htmlFor="notify-sms" className="cursor-pointer text-foreground">
                SMS
              </Label>
            </div>
          </div>
        </div>

        {/* Itens para Notificar */}
        {hasMethodSelected && (
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-foreground">Itens para Notificar</Label>
            <div className="space-y-2">
              {prescriptionItems.map((item, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <Checkbox
                    id={`item-${index}`}
                    checked={selectedItems.includes(item.prescription)}
                    onCheckedChange={() => toggleItem(item.prescription)}
                  />
                  <Label htmlFor={`item-${index}`} className="cursor-pointer flex-1 text-foreground">
                    <span className="font-medium">{item.prescription}</span>
                    {item.dosage && (
                      <span className="text-xs text-muted-foreground ml-2">({item.dosage})</span>
                    )}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Planejamento das Notificações */}
        {selectedItems.length > 0 && (
          <div className="space-y-4">
            <Label className="text-sm font-semibold text-foreground">Planejamento das Notificações</Label>
            <div className="space-y-4">
              {selectedItems.map((itemName) => {
                const config = itemsConfig[itemName];
                const item = prescriptionItems.find((i) => i.prescription === itemName);
                const isContinuous = config.durationDays === "continuous";

                return (
                  <Card key={itemName} className="p-4 bg-muted/30 border-border">
                    <div className="space-y-4">
                      <p className="font-medium text-foreground flex items-center gap-2">
                        💊 {itemName}
                      </p>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm text-foreground">Frequência</Label>
                          <Select
                            value={config.frequency}
                            onValueChange={(value) => updateItemConfig(itemName, "frequency", value)}
                          >
                            <SelectTrigger className="bg-background border-border text-foreground">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1 vez</SelectItem>
                              <SelectItem value="2">2 vezes</SelectItem>
                              <SelectItem value="3">3 vezes</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm text-foreground">Período</Label>
                          <Select
                            value={config.period}
                            onValueChange={(value) => updateItemConfig(itemName, "period", value)}
                          >
                            <SelectTrigger className="bg-background border-border text-foreground">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="day">Dia</SelectItem>
                              <SelectItem value="week">Semana</SelectItem>
                              <SelectItem value="month">Mês</SelectItem>
                              <SelectItem value="year">Ano</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-sm text-foreground">Horário</Label>
                          <Input
                            type="time"
                            value={config.time}
                            onChange={(e) => updateItemConfig(itemName, "time", e.target.value)}
                            className="bg-background border-border text-foreground"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm text-foreground">Dias da Semana</Label>
                        <div className="grid grid-cols-2 sm:flex sm:gap-2 gap-2">
                          {weekdayOptions.map((day) => (
                            <Button
                              key={day.value}
                              variant={config.weekdays.includes(day.value) ? "default" : "outline"}
                              size="sm"
                              className="sm:flex-1 w-full"
                              onClick={() => toggleWeekday(itemName, day.value)}
                            >
                              {day.label}
                            </Button>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm text-foreground">Prazo</Label>
                        {isContinuous ? (
                          <Input
                            value="Contínuo"
                            disabled
                            className="bg-muted border-border text-foreground"
                          />
                        ) : (
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              value={config.durationDays}
                              onChange={(e) => updateItemConfig(itemName, "durationDays", e.target.value)}
                              placeholder="30"
                              className="bg-background border-border text-foreground flex-1"
                            />
                            <span className="text-muted-foreground text-sm whitespace-nowrap">dias</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {isSaving ? "Salvando..." : "💾 Salvar Notificações"}
            </Button>

            {saveResult && (
              <div className="flex items-start gap-2 p-4 rounded-lg bg-primary/10 border border-primary/20">
                <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-foreground">{saveResult}</p>
                  <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                    {selectedItems.map((item) => {
                      const config = itemsConfig[item];
                      const methods = [];
                      if (notifyEmail) methods.push("E-mail");
                      if (notifyWhatsApp) methods.push("WhatsApp");
                      if (notifySMS) methods.push("SMS");

                      return (
                        <li key={item}>
                          • {item}: {config.frequency}x por {config.period === "day" ? "dia" : config.period === "week" ? "semana" : config.period === "month" ? "mês" : "ano"}, {config.weekdays.length} dia(s) da semana, por {config.durationDays} dias via {methods.join(", ")}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}
